import React, {FC} from 'react';
import {connect} from 'react-redux';

import {ResolveStateProps} from '../../../../../../libs/shared/core/src/store/helpers/commonReselectHelpers';
import {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {createSelector, makeMapStateToProps} from '../../../../../../srcCommon/helpers/reselectHelpers';
import {buildConversationPublicId} from '../../../../../helpers/models/conversationHelpers';
import {
  ActivityModel,
  ActivityVisualTypesEnum,
  DetailedActivityConversationLinkedModel,
  DetailedVisualTypesEnum,
} from '../../../../../models/api/activityModel';
import {ActivitySourceVisualTypesEnum} from '../../../../../models/api/activitySourceModel';
import {AppState} from '../../../../../store/appState';
import {makeGetHasSameRequestorAsPrevious} from '../../../../../store/data/conversations/linkedConversationsSelectors';
import {getConversations} from '../../../../../store/data/dataSelectors';
import {getFindTeammate} from '../../../../../store/data/teammates/teammatesSelectors';
import {buildLinkedConversationUnfurl} from '../../conversation/linkedConversations/linkConversationsHelpers';
import {LinkedConversationViewer} from './linkedConversationsViewer';

/*
 * Props.
 */

interface LinkedConversationsViewerContainerProps {
  panelId: ViewerPanelId;
  activity: DetailedActivityConversationLinkedModel;
  previousActivity: ActivityModel | undefined;
}

type LinkedConversationsViewerStateProps = ResolveStateProps<typeof mapStateToProps>;
type LinkedConversationsViewerContainerComponentProps = LinkedConversationsViewerContainerProps &
  LinkedConversationsViewerStateProps;

/*
 * Component.
 */

const LinkedConversationsViewerContainerComponent: FC<LinkedConversationsViewerContainerComponentProps> = (
  props,
) => {
  const {
    panelId,
    activity,
    currentConversation,
    linkedConversation,
    linkedConversationPublicId,
    sourceTeammate,
    hasSameRequestorAsPrevious,
    areBothUnlinkedActivities,
  } = props;

  const unfurl =
    linkedConversation && linkedConversationPublicId
      ? buildLinkedConversationUnfurl(
          linkedConversation,
          linkedConversationPublicId,
          activity.linkedConversationPermalink,
        )
      : undefined;

  if (!currentConversation) {
    return null;
  }

  return (
    <LinkedConversationViewer
      panelId={panelId}
      activity={activity}
      currentConversation={currentConversation}
      linkedConversation={linkedConversation}
      sourceTeammate={sourceTeammate}
      commentUnfurl={unfurl}
      shouldHideTitle={hasSameRequestorAsPrevious || areBothUnlinkedActivities}
    />
  );
};

/*
 * Selectors.
 */

function getActivity(_state: AppState, props: LinkedConversationsViewerContainerProps) {
  return props.activity;
}

function getPreviousActivity(_state: AppState, props: LinkedConversationsViewerContainerProps) {
  return props.previousActivity;
}

function makeGetSourceTeammate() {
  return createSelector([getActivity, getFindTeammate], (activity, findTeammate) => {
    const {source} = activity;
    if (source.visualType !== ActivitySourceVisualTypesEnum.TEAMMATE) {
      return undefined;
    }

    return findTeammate(source.teammateId);
  });
}

function makeGetCurrentConversation() {
  return createSelector(
    [getActivity, getConversations],
    (activity, conversations) => conversations[activity.conversationId],
  );
}

function makeGetLinkedConversation() {
  return createSelector([getActivity, getConversations], (activity, conversations) =>
    activity.linkedConversationId ? conversations[activity.linkedConversationId] : undefined,
  );
}

function makeGetLinkedConversationPublicId() {
  return createSelector([makeGetLinkedConversation()], (linkedConversation) =>
    linkedConversation ? buildConversationPublicId(linkedConversation) : undefined,
  );
}

function makGetAreBothUnlinkActivities() {
  return createSelector([getActivity, getPreviousActivity], (activity, previousActivity) => {
    if (!previousActivity) {
      return false;
    }
    return (
      previousActivity.visualType === ActivityVisualTypesEnum.DETAILED &&
      previousActivity.detailedVisualType === DetailedVisualTypesEnum.CONVERSATION_LINKED &&
      previousActivity.linkedConversationId === undefined &&
      activity.visualType === ActivityVisualTypesEnum.DETAILED &&
      activity.detailedVisualType === DetailedVisualTypesEnum.CONVERSATION_LINKED &&
      activity.linkedConversationId === undefined
    );
  });
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<LinkedConversationsViewerContainerProps>()(() => ({
  currentConversation: makeGetCurrentConversation(),
  linkedConversation: makeGetLinkedConversation(),
  linkedConversationPublicId: makeGetLinkedConversationPublicId(),
  sourceTeammate: makeGetSourceTeammate(),
  hasSameRequestorAsPrevious: makeGetHasSameRequestorAsPrevious(getActivity, getPreviousActivity),
  areBothUnlinkedActivities: makGetAreBothUnlinkActivities(),
}));

export const LinkedConversationsViewerContainer = connect(mapStateToProps)(
  LinkedConversationsViewerContainerComponent,
);
